import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Spinner, toast } from "@heroui/react";
import { useEffect } from "react";
import type { BlacklistItem } from "@repo/api/blacklist.type";
import { useAuth } from "@repo/context/auth/auth";
import { useTemplateProtectionGuidelines } from "@repo/context/template-protection-guidelines";
import { ComponentComfirm } from "@repo/ui/components/Modals";
import { BlacklistSetListItem } from "@repo/ui/pages/template_service/blacklist/BlacklistSetListItem";
import { formatAddedAt } from "@repo/ui/pages/template_service/blacklist/utils";
import {
  useSubscribedBlacklists,
  useUnsubscribeBlacklist,
} from "@repo/ui/pages/template_service/blacklist/use-blacklist";

export const Route = createFileRoute("/template_protections/blacklist/subscribed")({
  component: SubscribedBlacklistPage,
});

const MAX_SUBSCRIBED_BLACKLISTS = 10;

export default function SubscribedBlacklistPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { hasAgreedToGuidelines } = useTemplateProtectionGuidelines();
  const { data: list = [], isPending, error } = useSubscribedBlacklists(isAuthenticated === true);
  // 未登录时不请求，isPending 会一直为 true，这里按非加载态处理
  const loading = isAuthenticated === true && isPending;
  const unsubscribe = useUnsubscribeBlacklist();
  const actionId = unsubscribe.isPending ? unsubscribe.variables : null;

  useEffect(() => {
    if (error) {
      toast.danger((error as { msg?: string }).msg || "加载失败");
    }
  }, [error]);

  // 错误 toast 在 hook 里统一处理，这里吞掉 reject 让确认框正常关闭
  const handleUnsubscribe = (item: BlacklistItem) =>
    unsubscribe.mutateAsync(item.id).then(
      () => undefined,
      () => undefined,
    );

  return (
    <div className="px-4 pt-3 space-y-3">
        <p className="text-sm text-gray-500">
          已订阅{" "}
          <span className="font-semibold text-foreground">
            {loading ? "…" : list.length}
          </span>{" "}
          / {MAX_SUBSCRIBED_BLACKLISTS}
        </p>

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : list.length === 0 ? (
          <div className="py-10 text-center text-gray-500 text-sm">
            暂无订阅，前往
            <button
              type="button"
              className="text-primary underline mx-1"
              onClick={() =>
                navigate({ to: "/template_protections/blacklist/public" })
              }
            >
              公共黑名单
            </button>
            订阅
          </div>
        ) : (
          list.map((item) => (
            <BlacklistSetListItem
              key={item.id}
              item={item}
              dateLabel={`订阅时间 ${formatAddedAt(item.updated_at)}`}
              onPress={() =>
                navigate({
                  to: "/template_protections/blacklist/public/$id",
                  params: { id: item.id },
                })
              }
              onOwnerPress={() =>
                navigate({
                  to: "/template_protections/trusted-query",
                  search: { search: item.uid },
                })
              }
              actions={
                <ComponentComfirm
                  buttonTitle="取消订阅"
                  buttonProps={{
                    size: "sm",
                    variant: "danger-soft",
                    className: "rounded-full",
                    isDisabled: !hasAgreedToGuidelines,
                  }}
                  title="取消订阅"
                  body={
                    <p>
                      确认取消订阅
                      <span className="font-semibold mx-1">{item.name}</span>？
                    </p>
                  }
                  onConfirm={() => handleUnsubscribe(item)}
                  loading={actionId === item.id}
                />
              }
            />
          ))
        )}
    </div>
  );
}
